import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import axios from 'axios';
import { API_BASE_URL } from './api';

export default function FooterNav({
  coderName,
  currentIndex,
  goToVideo,
  projectSlug,
  videoId,
  currentResponse,
  totalVideos,
  setTotalVideos,
  filter,
  setFilter,
  filterHasNoResults
}) {
  const [coderProgress, setCoderProgress] = useState({ submitted: [], saved: [] });
  const [videoIdMap, setVideoIdMap] = useState({}); // Map of index to video ID
  const [inputValue, setInputValue] = useState(currentIndex + 1);
  const [debounceTimer, setDebounceTimer] = useState(null);

  // Update input value when currentIndex changes
  useEffect(() => {
    setInputValue(currentIndex + 1);
  }, [currentIndex]);

  useEffect(() => {
    // Ensure we have totalVideos from first load
    if (!projectSlug || !coderName || totalVideos > 0) return;

    axios.get(`${API_BASE_URL}/api/video-at-index`, {
      params: { project: projectSlug, coder: coderName, index: currentIndex }
    })
      .then(res => {
        if (res.data?.total) {
          setTotalVideos(res.data.total);
        }
      })
      .catch(err => {
        console.error("Failed to fetch total video count:", err);
      });
  }, [projectSlug, coderName, currentIndex, totalVideos, setTotalVideos]);


  // Fetch coder's progress data
  useEffect(() => {
    if (!projectSlug || !coderName || !totalVideos) return;

    axios.get(`${API_BASE_URL}/api/projects`)
      .then(res => {
        const project = res.data.find(p => p.slug === projectSlug);
        if (project && project.responses && project.responses[coderName]) {
          const responses = project.responses[coderName] || [];
          const submittedVideos = [];
          const excludedVideos = [];
          
          // Parse responses to separate submitted, excluded, and saved videos
          const savedVideos = [];
          responses.forEach(response => {
            if (response.excluded) {
              excludedVideos.push(response.video_index || response.video_id);
            } else if (response.status === 'submitted') {
              submittedVideos.push(response.video_index || response.video_id);
            } else if (response.status === 'draft') {
              savedVideos.push(response.video_index || response.video_id);
            }
          });
          
          
          setCoderProgress({
            submitted: submittedVideos,
            excluded: excludedVideos,
            saved: savedVideos
          });
        }
      })
      .catch(err => {
        console.error("Failed to fetch coder progress:", err);
      });
  }, [projectSlug, coderName, totalVideos, filter]);

  const handleNavigation = async (newIndex) => {
    if (!coderName.trim()) return;

    // Skip save if already submitted - prevent overwriting submitted status
    const isSubmitted = currentResponse.status === "submitted";
    if (isSubmitted) {
      goToVideo(newIndex);
      return;
    }

    const categoriesOnly = Object.fromEntries(
      Object.entries(currentResponse).filter(([key]) => key !== 'notes' && key !== 'status')
    );

    try {
      await axios.post(`${API_BASE_URL}/api/save-progress`, {
        project: projectSlug,
        coder: coderName,
        video_id: videoId,
        response: {
          ...categoriesOnly,
          notes: currentResponse.notes || ""
        }
      });
      goToVideo(newIndex);
    } catch (err) {
      console.error('Error saving progress before navigation:', err);
    }
  };

  const renderProgressBar = () => {
    if (!totalVideos) return null;

    const progressItems = [];
    
    // Use the actual total videos for the current context (filtered or all)
    const totalVideosToShow = totalVideos;
    
    
    for (let i = 0; i < totalVideosToShow; i++) {
      let status = 'unstarted';
      
      // Apply colors based on current filter context
      if (filter === 'uncoded') {
        // For uncoded filter, all segments should be gray (unstarted)
        status = 'unstarted';
      } else if (filter === 'saved') {
        // For saved filter, show saved videos
        if (coderProgress.saved && coderProgress.saved.length > 0 && i < coderProgress.saved.length) {
          status = 'saved';
        }
      } else if (filter === 'submitted') {
        // For submitted filter, show submitted videos
        if (coderProgress.submitted && coderProgress.submitted.length > 0 && i < coderProgress.submitted.length) {
          status = 'submitted';
        }
      } else if (filter === 'excluded') {
        // For excluded filter, show excluded videos
        if (coderProgress.excluded && coderProgress.excluded.length > 0 && i < coderProgress.excluded.length) {
          status = 'excluded';
        }
      } else {
        // For 'all' filter, show mixed progress
        if (coderProgress.excluded && coderProgress.excluded.length > 0 && i < coderProgress.excluded.length) {
          status = 'excluded';
        } else if (coderProgress.submitted && coderProgress.submitted.length > 0 && i < coderProgress.submitted.length) {
          status = 'submitted';
        } else if (coderProgress.saved && coderProgress.saved.length > 0 && i < coderProgress.saved.length) {
          status = 'saved';
        }
      }
      
      const isCurrent = i === currentIndex;
      
      progressItems.push(
        <div
          key={i}
          className={`progress-segment ${status} ${isCurrent ? 'current' : ''}`}
          style={{
            flex: '1 1 0',
            minWidth: 0,
            height: '20px',
            boxSizing: 'border-box',
            backgroundColor: status === 'excluded' ? '#dc3545' :
                           status === 'submitted' ? '#28a745' : 
                           status === 'saved' ? '#ffc107' : '#e9ecef',
            border: isCurrent ? '2px solid #007bff' : 
                    status === 'excluded' ? '1px solid #dc3545' :
                    status === 'submitted' ? '1px solid #28a745' : 
                    status === 'saved' ? '1px solid #ffc107' : '1px solid #e9ecef',
            position: 'relative',
            cursor: 'pointer'
          }}
          onClick={() => handleNavigation(i)}
          title={`Video ${i + 1}${isCurrent ? ' (current)' : ''} - ${status}`}
        >
          {isCurrent && (
            <div
              style={{
                position: 'absolute',
                top: '-8px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '0',
                height: '0',
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderBottom: '8px solid #007bff',
                zIndex: 1
              }}
            />
          )}
        </div>
      );
    }

    return (
      <div
        className="d-flex w-100"
        style={{
          height: '20px',
          minWidth: 0,
          borderRadius: '4px',
          overflow: 'hidden',
          border: '1px solid #dee2e6'
        }}
      >
        {progressItems}
      </div>
    );
  };

  return (
    <div className="d-flex w-100 align-items-center gap-3">
      <div className="col-auto d-flex justify-content-center align-items-center">
        <Button
          onClick={() => handleNavigation(Math.max(currentIndex - 1, 0))}
          disabled={!coderName.trim() || currentIndex === 0}
        >
          Previous
        </Button>
      </div>

      <div className="col d-flex flex-column justify-content-center align-items-center gap-1" style={{ minWidth: 0 }}>
        <div className="d-flex align-items-center gap-2 mb-2">
          <label htmlFor="video-jump" className="mb-0">Video</label>
          <input
            id="video-jump"
            type="number"
            min="1"
            max={totalVideos}
            value={inputValue}
            onChange={(e) => {
              const value = e.target.value;
              setInputValue(value);
              
              // Clear existing timer
              if (debounceTimer) {
                clearTimeout(debounceTimer);
              }
              
              // Set new timer to navigate after 1 second of no typing
              const newTimer = setTimeout(() => {
                const newIndex = parseInt(value, 10) - 1;
                if (!isNaN(newIndex) && newIndex >= 0 && newIndex < totalVideos) {
                  handleNavigation(newIndex);
                }
              }, 1000);
              
              setDebounceTimer(newTimer);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                // Clear timer and navigate immediately on Enter
                if (debounceTimer) {
                  clearTimeout(debounceTimer);
                }
                const newIndex = parseInt(inputValue, 10) - 1;
                if (!isNaN(newIndex) && newIndex >= 0 && newIndex < totalVideos) {
                  handleNavigation(newIndex);
                }
              }
            }}
            className="form-control form-control-sm"
            style={{ width: '80px' }}
          />
          <span>/ {totalVideos}</span>
          
          {/* Filter Buttons - Inline with video counter */}
          <div className="d-flex gap-1 ms-3">
            <Button
              size="sm"
              variant={filter === 'all' ? 'primary' : 'outline-secondary'}
              onClick={() => setFilter('all')}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
              className={filter === 'all' ? 'fw-bold' : ''}
            >
              All
            </Button>
            <Button
              size="sm"
              variant={filter === 'uncoded' ? 'primary' : 'outline-secondary'}
              onClick={() => setFilter('uncoded')}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
              className={filter === 'uncoded' ? 'fw-bold' : ''}
            >
              Uncoded
            </Button>
            <Button
              size="sm"
              variant={filter === 'saved' ? 'primary' : 'outline-secondary'}
              onClick={() => setFilter('saved')}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
              className={filter === 'saved' ? 'fw-bold' : ''}
            >
              Draft
            </Button>
            <Button
              size="sm"
              variant={filter === 'submitted' ? 'primary' : 'outline-secondary'}
              onClick={() => setFilter('submitted')}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
              className={filter === 'submitted' ? 'fw-bold' : ''}
            >
              Submitted
            </Button>
            <Button
              size="sm"
              variant={filter === 'excluded' ? 'primary' : 'outline-secondary'}
              onClick={() => setFilter('excluded')}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
              className={filter === 'excluded' ? 'fw-bold' : ''}
            >
              Excluded
            </Button>
          </div>
        </div>
        
        
        {renderProgressBar()}
      </div>

      <div className="col-auto d-flex justify-content-center align-items-center">
        <Button
          onClick={() => handleNavigation(Math.min(currentIndex + 1, totalVideos - 1))}
          disabled={!coderName.trim() || currentIndex >= totalVideos - 1}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
